import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import Form from '../../components/form/form';
import SubmitButton from '../../components/submit-button/submit-button';

// Form Data
import { formDataGalleryCreate } from '../../form-data/form-data-gallery-create';

// Helpers
import {
  getGalleryData,
  handleFormOnChange,
  deleteAttachment,
  maxAttachments,
  uploadDroppedFiles,
  useRevokePreviewUrlsOnUnmount,
  handleStudentChange,
  checkIfFirstStepWasFilled,
  checkIfAllStepsWereFilled,
  deleteGallery,
  buildGalleryRequest,
  filesToImagePreviewList,
} from '../../helpers/gallery-helper';
import { recipientsToStudentIds } from '../../helpers/student-uuid-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ClassroomService from '../../services/classroom-service';
import GalleryService from '../../services/gallery-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';
import FileStorageService from '../../services/file-storage-service';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';
import ImagePreview from '../../components/image-preview/image-preview';
import StudentSelector from '../../components/student-selector/student-selector';

const GalleryEdit = () => {
  const navigate = useNavigate();
  const { setToastContent, setModalContent } = useContext(GeneralContext);
  const { galleryId } = useParams<{ galleryId: string }>();
  const [error, setError] = useState(false);

  const [data, setData] = useState<GalleryFormData>(null);
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const [validations, setValidations] = useState({});
  const [allowNextPage, setAllowNext] = useState(false);
  const [allowSubmit, setAllowSubmit] = useState(false);

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useRevokePreviewUrlsOnUnmount(data);

  useEffect(() => {
    checkIfFirstStepWasFilled(data, validations, allowNextPage, setAllowNext);
    checkIfAllStepsWereFilled(data, validations, allowSubmit, setAllowSubmit);
  }, [validations, allowNextPage, allowSubmit, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [classroomList, setClassroomList] = useState<
    ClassroomStudents[] | null
  >(null);
  const [galleryService, setGalleryService] = useState<GalleryService | null>(null);
  const [fileStorageService, setFileStorageService] = useState<FileStorageService | null>(null);

  useEffect(() => {
    const classroomService = new ClassroomService();
    const schoolService = new SchoolService();
    const storageService = new StorageService();
    const schoolId = storageService.getEmployee().schoolId;

    Promise.all([
      classroomService.listClassroomStudents(),
      schoolService.getSchoolById(schoolId),
    ])
      .then(([classrooms, school]) => {
        const service = new GalleryService(school.uuid);

        setClassroomList(classrooms);
        setGalleryService(service);
        setFileStorageService(new FileStorageService(school.uuid));

        // Rota canônica (e não a do BFF): o formulário precisa dos fileIds e dos
        // uuids de aluno crus para devolvê-los inteiros no PUT, que substitui as
        // duas listas por completo.
        return service.getGallery(galleryId).then((gallery) => {
          const galleryData = getGalleryData(gallery);
          const { classroomIds, studentIds, unmatchedStudentUuids } = recipientsToStudentIds(
            gallery.recipients,
            classrooms
          );

          galleryData.classData = classroomIds;
          galleryData.studentData = studentIds;
          galleryData.unmatchedStudentUuids = unmatchedStudentUuids;

          setData(galleryData);
        });
      })
      .catch(() => setError(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileDrop = (item) => {
    if (!item) return;

    uploadDroppedFiles({
      files: item.files,
      data,
      setData,
      fileStorageService,
      setIsLoadingAttachment,
      setUploadProgress,
      setModalContent,
      setToastContent,
    });
  };

  const handleSubmit = () => {
    // Um duplo clique criaria duas galerias, cada uma com o seu push.
    if (isSubmitting) return;
    setIsSubmitting(true);

    galleryService
      .updateGallery(galleryId, buildGalleryRequest(data, classroomList))
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Galeria enviada!',
          type: 'success',
        });
        navigate('/galerias/listar');
      })
      .catch(() => {
        setIsSubmitting(false);
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar a galeria.',
          type: 'error',
        });
      });
  };

  const handleNextStep = () => {
    setStep(1)
  }

  const handlePreviousStep = () => {
    setStep(0)
  }

  if (error) {
    return <ErrorComponent type='contentFull' />;
  }

  if (!data || !classroomList || !galleryService || !fileStorageService)
    return <Loading type='contentFull' />;

  if (step === 0)
    return (<BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <Form
        formData={formDataGalleryCreate(data)}
        validations={validations}
        handleOnChange={(evt) => handleFormOnChange(evt, validations, setValidations, setData)}
      />
      {classroomList && (
        <ImagePreview
          maxFiles={maxAttachments}
          imageList={filesToImagePreviewList(data.formData.attachments)}
          onDrop={handleFileDrop}
          onDelete={(fileId: string) => deleteAttachment(fileId, setData)}
          uploadProgress={uploadProgress}
          isUploading={isLoadingAttachment}
        />
      )}
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/galerias/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='deletar'
          variant='terciary'
          onClick={() =>
            deleteGallery(
              galleryService,
              galleryId,
              setModalContent,
              setToastContent,
              navigate
            )
          }
        />
        <SubmitButton
          value='próximo'
          variant='primary'
          onClick={handleNextStep}
          disabled={!allowNextPage}
        />
      </ButtonsBar>
    </BaseContentContainer>);
  else
    return (<BaseContentContainer
      format={BaseContentContainerFormat.singleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      {/* BaseContentContainer posiciona os filhos pelo índice: o aviso tem de ficar
          no mesmo filho do seletor, ou a barra de botões deixa de ser renderizada. */}
      <>
        {classroomList && (
          <StudentSelector
            classrooms={classroomList}
            handleStudentChange={(
              selectedClassrooms: number[],
              selectedStudents: number[]
            ) =>
              handleStudentChange(
                selectedClassrooms,
                selectedStudents,
                data,
                setData
              )
            }
            initialClassrooms={data.classData}
            initialStudents={data.studentData}
          />
        )}
        {data.unmatchedStudentUuids.length > 0 && (
          <p>
            {data.unmatchedStudentUuids.length} aluno(s) que receberam esta galeria não estão nas
            turmas listadas e continuam como destinatários.
          </p>
        )}
      </>

      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <SubmitButton value='voltar' variant='terciary' onClick={handlePreviousStep} />
        <SubmitButton
          value='publicar galeria'
          variant='primary'
          onClick={handleSubmit}
          disabled={!allowSubmit || isSubmitting}
        />
      </ButtonsBar>
    </BaseContentContainer>);
};

export default GalleryEdit;
